import React, { useEffect, useRef, useState } from "react";
import "../Styles/DobWheelPicker.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ITEM_HEIGHT = 40;
const MIN_YEAR = 1900;

const getMaximumDate = () => {
  const today = new Date();
  return new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
};

const getDateValue = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1
      ? date
      : null;
  }

  return null;
};

const WheelColumn = ({ items, selectedValue, onChange, type }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const selectedIndex = items.findIndex(
      (item) => item.value === selectedValue,
    );

    if (selectedIndex !== -1) {
      containerRef.current.scrollTop = selectedIndex * ITEM_HEIGHT;
    }
  }, [items.length, selectedValue]);

  const handleScroll = () => {
    if (!containerRef.current) {
      return;
    }

    const index = Math.round(containerRef.current.scrollTop / ITEM_HEIGHT);
    const item = items[Math.max(0, Math.min(index, items.length - 1))];

    if (item && item.value !== selectedValue) {
      onChange(item.value);
    }
  };

  return (
    <div className={`dob-wheel-column dob-wheel-${type}`}>
      <div
        ref={containerRef}
        className="dob-wheel-scroll"
        onScroll={handleScroll}
        role="listbox"
        aria-label={type}
      >
        <div className="dob-wheel-spacer" />
        {items.map((item) => (
          <button
            key={item.value}
            type="button"
            role="option"
            aria-selected={item.value === selectedValue}
            className={`dob-wheel-item ${
              item.value === selectedValue ? "dob-wheel-item-selected" : ""
            }`}
            onClick={() => onChange(item.value)}
          >
            {item.label}
          </button>
        ))}
        <div className="dob-wheel-spacer" />
      </div>
    </div>
  );
};

export default function DobWheelPicker({
  value,
  onChange,
  className = "custom-date-input dob-display-input",
  wrapperClassName = "date-input-wrapper",
  placeholder = "dd-mm-yyyy",
  style,
  id,
}) {
  const [open, setOpen] = useState(false);
  const maxDate = getMaximumDate();
  const selectedDate = getDateValue(value) || maxDate;
  const selectedDay = selectedDate.getDate();
  const selectedMonth = selectedDate.getMonth();
  const selectedYear = selectedDate.getFullYear();
  const maxYear = maxDate.getFullYear();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  const monthCount = selectedYear === maxYear ? maxDate.getMonth() + 1 : 12;
  const months = MONTHS.slice(0, monthCount).map((month, index) => ({
    value: index,
    label: month,
  }));

  const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
  const dayCount =
    selectedYear === maxYear && selectedMonth === maxDate.getMonth()
      ? Math.min(daysInMonth, maxDate.getDate())
      : daysInMonth;
  const days = Array.from({ length: dayCount }, (_, index) => ({
    value: index + 1,
    label: String(index + 1).padStart(2, "0"),
  }));
  const years = Array.from(
    { length: Math.max(0, maxYear - MIN_YEAR + 1) },
    (_, index) => ({ value: maxYear - index, label: String(maxYear - index) }),
  );

  const updateDate = (day, month, year) => {
    const validDay = Math.min(day, new Date(year, month + 1, 0).getDate());
    const date = new Date(year, month, validDay);

    if (date <= maxDate) {
      onChange(date);
    }
  };

  const formatDate = (date) => {
    const parsedDate = getDateValue(date);
    if (!parsedDate) {
      return "";
    }

    return `${String(parsedDate.getDate()).padStart(2, "0")}-${String(
      parsedDate.getMonth() + 1,
    ).padStart(2, "0")}-${parsedDate.getFullYear()}`;
  };

  const closePicker = () => setOpen(false);

  return (
    <div className={`dob-wheel-picker ${wrapperClassName}`} style={style}>
      <button
        type="button"
        id={id}
        className={className}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span className={value ? "" : "dob-placeholder"}>
          {formatDate(value) || placeholder}
        </span>
        <img
          className="calender-icon"
          src={require("../assests/images/calender-icon.png")}
          alt=""
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="dob-picker-overlay" onClick={closePicker}>
          <div
            className="dob-picker-container"
            role="dialog"
            aria-modal="true"
            aria-label="Select Date of Birth"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="dob-picker-header">
              <span>Select Date of Birth</span>
              <button
                type="button"
                className="dob-picker-close"
                aria-label="Close date picker"
                onClick={closePicker}
              >
                ×
              </button>
            </div>

            <div className="dob-wheel-headings">
              <div>Day</div>
              <div>Month</div>
              <div>Year</div>
            </div>

            <div className="dob-wheel-wrapper">
              <WheelColumn
                type="day"
                items={days}
                selectedValue={selectedDay}
                onChange={(day) => updateDate(day, selectedMonth, selectedYear)}
              />
              <WheelColumn
                type="month"
                items={months}
                selectedValue={selectedMonth}
                onChange={(month) =>
                  updateDate(selectedDay, month, selectedYear)
                }
              />
              <WheelColumn
                type="year"
                items={years}
                selectedValue={selectedYear}
                onChange={(year) =>
                  updateDate(selectedDay, selectedMonth, year)
                }
              />
              <div className="dob-wheel-selection-line" />
            </div>

            <div className="dob-selected-value">
              {String(selectedDay).padStart(2, "0")}-
              {String(selectedMonth + 1).padStart(2, "0")}-{selectedYear}
            </div>

            <button
              type="button"
              className="dob-picker-done"
              onClick={closePicker}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
